import { ApiClient } from '../services/ApiClient.js';
import { Notification } from '../services/Notification.js';
import { translate } from './i18n.js';

export class Contact {
    constructor(app, options = {}) {
        this.app = app;
        this.api = options.api || app?.account?.api || new ApiClient(this.getApiConfig(app), app);
        this.event = null;
    }

    getApiConfig(app) {
        const apiConfig = app?.config?.api || {};

        return {
            ...apiConfig,
            publicToken: apiConfig.publicToken || apiConfig.generic_token || 'PUBLIC'
        };
    }

    getContents() {
        return {
            contact: {
                titleKey: 'home.contactTitle',
                icon: '/images/contact-grey.svg',
                path: 'contact',
                attachedLabel: true,
                render: (context) => this.render(context),
                cleanup: () => this.cleanup()
            }
        };
    }

    openForEvent(event) {
        if (!event?.uid || !this.app?.simpleModal) {
            return false;
        }

        this.event = event;
        const opened = this.app.simpleModal.open('contact');

        if (!opened) {
            this.event = null;
        }

        return opened;
    }

    render(context) {
        const { body, locale } = context;
        const intro = document.createElement('p');
        const contents = [intro];

        body.classList.add('contact-modal-body');
        intro.className = 'contact-intro';
        intro.textContent = translate(
            this.event?.uid ? 'contact.eventIntro' : 'contact.intro',
            locale
        );
        if (this.event?.uid) {
            contents.push(this.createEventContext(locale));
        }
        contents.push(this.createForm(context));
        body.replaceChildren(...contents);
    }

    createEventContext(locale) {
        const eventContext = document.createElement('p');
        const label = document.createElement('strong');

        eventContext.className = 'contact-event-context';
        label.textContent = `${translate('contact.event', locale)} : `;
        eventContext.append(label, document.createTextNode(this.getEventName(locale)));

        return eventContext;
    }

    getEventName(locale) {
        const name = this.event?.name;

        if (!name || typeof name !== 'object') {
            return String(name || '');
        }

        return name[locale] || name.fr || Object.values(name)[0] || '';
    }

    cleanup() {
        this.event = null;
    }

    createForm(context) {
        const form = document.createElement('form');
        const buttons = document.createElement('div');
        const user = this.app?.user || {};
        const realname = user.realname || [user.prenom, user.nom].filter(Boolean).join(' ');
        const organisation = this.createField('organisation', 'text', 'contact.organisation', context.locale, {
            autocomplete: 'organization',
            value: user.organisme_name || user.organisation_name || ''
        });

        form.className = 'ui-form contact-form';
        form.noValidate = true;
        buttons.className = 'ui-form-buttons';
        buttons.append(this.createSubmit(context.locale));
        form.append(
            this.createField('realname', 'text', 'contact.realname', context.locale, {
                autocomplete: 'name',
                value: realname,
                required: true
            }),
            organisation,
            this.createField('email', 'email', 'contact.email', context.locale, {
                autocomplete: 'email',
                value: user.email || '',
                required: true
            }),
            this.createField('telephone', 'tel', 'contact.telephone', context.locale, {
                autocomplete: 'tel',
                value: user.telephone || user.phone || ''
            }),
            this.createMessageField(context.locale),
            buttons
        );
        form.addEventListener('submit', (event) => this.submit(event, context));
        if (user.organisme && !organisation.querySelector('input')?.value) {
            void this.prefillOrganisationName(organisation.querySelector('input'), user.organisme);
        }

        return form;
    }

    async prefillOrganisationName(input, organisationUid) {
        if (!input || !this.app?.account?.getOrganisations) return false;

        try {
            const response = await this.app.account.getOrganisations();
            const groups = response?.result?.groups || [];
            const organisations = response?.result?.organismes
                || groups.flatMap((group) => group.organismes || []);
            const organisation = organisations.find((item) => (
                String(item?.uid || item?.uid_organismes || '') === String(organisationUid)
            ));

            if (!input.isConnected || String(input.value || '').trim() || !organisation) return false;
            input.value = String(organisation.name || organisation.nom || '').trim();
            input.dispatchEvent(new Event('input'));
            return Boolean(input.value);
        } catch {
            return false;
        }
    }

    createField(name, type, labelKey, locale, options = {}) {
        const wrapper = document.createElement('div');
        const label = document.createElement('label');
        const input = document.createElement('input');
        const inputId = `contact-${name}`;

        wrapper.className = 'ui-form-element contact-field';
        label.className = 'ui-input-label';
        label.htmlFor = inputId;
        label.textContent = translate(labelKey, locale);
        input.id = inputId;
        input.className = 'ui-input';
        input.name = name;
        input.type = type;
        input.required = Boolean(options.required);
        input.autocomplete = options.autocomplete || 'off';
        input.value = options.value || '';
        input.spellcheck = type !== 'email' && type !== 'tel';
        this.bindValueState(input);
        wrapper.append(label, input);

        return wrapper;
    }

    createMessageField(locale) {
        const wrapper = document.createElement('div');
        const label = document.createElement('label');
        const textarea = document.createElement('textarea');
        const inputId = 'contact-message';

        wrapper.className = 'ui-form-element contact-field contact-field-message';
        label.className = 'ui-input-label';
        label.htmlFor = inputId;
        label.textContent = translate('contact.message', locale);
        textarea.id = inputId;
        textarea.className = 'ui-textarea';
        textarea.name = 'message';
        textarea.required = true;
        textarea.rows = 7;
        this.bindValueState(textarea);
        wrapper.append(label, textarea);

        return wrapper;
    }

    bindValueState(input) {
        const sync = () => {
            input.classList.toggle('has-value', String(input.value || '').trim() !== '');
        };

        input.addEventListener('input', sync);
        input.addEventListener('change', sync);
        sync();
    }

    createSubmit(locale) {
        const button = document.createElement('button');
        const icon = document.createElement('img');
        const label = document.createElement('span');

        button.type = 'submit';
        button.className = 'ui-buttons big';
        icon.src = '/images/contact-grey.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        label.textContent = translate('contact.send', locale);
        button.append(icon, label);

        return button;
    }

    async submit(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity()) {
            return false;
        }

        const data = new FormData(form);

        data.append('from_web', '1');
        data.append('_silentErrors', 'true');
        if (this.event?.uid) {
            data.append('event_uid', this.event.uid);
        }

        const sent = await this.api.query(
            'post',
            '/contact/send',
            data,
            () => {
                this.setBusy(form, true, context.locale);
                context.showLoading();
            },
            () => {
                context.hideLoading();
            },
            null,
            {
                onError: () => {
                    context.hideLoading();
                    this.setBusy(form, false, context.locale);
                }
            }
        );

        if (sent) {
            this.renderSuccess(context);
            return true;
        }

        this.setBusy(form, false, context.locale);
        new Notification({
            icon: 'warning.svg',
            title: translate('contact.errorTitle', context.locale),
            content: translate('contact.errorMessage', context.locale),
            class: 'no-icon-filter'
        });

        return false;
    }

    renderSuccess(context, options = {}) {
        const success = document.createElement('div');
        const icon = document.createElement('img');
        const title = document.createElement('strong');
        const message = document.createElement('p');
        const scroll = context.scroll;
        const titleKey = options.titleKey || 'contact.successTitle';
        const messageKey = options.messageKey || 'contact.successMessage';

        success.className = 'contact-success';
        success.setAttribute('role', 'status');
        success.setAttribute('aria-live', 'polite');
        icon.className = 'contact-success-icon no-icon-filter';
        icon.src = '/images/check.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        title.className = 'contact-success-title';
        title.textContent = translate(titleKey, context.locale);
        message.className = 'contact-success-message';
        message.textContent = translate(messageKey, context.locale);
        success.append(icon, title, message);
        context.modal.classList.add('contact-modal-success-state');
        context.body.replaceChildren(success);
        if (scroll) {
            scroll.scrollTop = 0;
        }
    }

    setBusy(form, busy, locale) {
        form.classList.toggle('loading', busy);
        form.querySelectorAll('input, select, textarea, button').forEach((element) => {
            element.disabled = busy;
        });

        const label = form.querySelector('[type="submit"] span');

        if (label) {
            label.textContent = translate(busy ? 'contact.sending' : 'contact.send', locale);
        }
    }
}
